import { buildTimeline } from "./program-timeline";

import type { TimelineGroup, TimelineRow } from "./program-timeline";

/** The statuses of the status chips, in order. */
const ALL_STATUSES = ["active", "draft", "paused", "finished", "archived"];

/**
 * The statuses that "All statuses" and "Show all statuses" turn on: each one but archived. An
 * archived Communication shows only when its own chip is on.
 */
export const ALL_BUT_ARCHIVED = ALL_STATUSES.filter((status) => status !== "archived");

/** The statuses that are on at the start: paused, finished and archived ones do not show. */
export const DEFAULT_STATUSES = ["active", "draft"];

/**
 * The statuses that are on, and the events, the keys of groups, that are off. Events come from the
 * rows, so the filter keeps the ones that are off, and an event that it does not name is on.
 */
export interface TimelineFilter {
  statuses: string[];
  hiddenEvents: string[];
}

/** A filter chip: "all", a status or an event, with its number of rows and if it is on. */
interface TimelineChip {
  key: string;
  count: number;
  selected: boolean;
}

/** The chips, the rows and the groups that a filter shows. */
export interface TimelineView {
  /**
   * "all", then each status that a row has, with its number of rows. "all" counts the rows that
   * are not archived, and is on when each of their statuses is on.
   */
  statuses: TimelineChip[];
  /**
   * "all", then each event that a row with a status that is on has, with its name, its colour and
   * its number of those rows. "all" is on when each of those events is on.
   */
  events: (TimelineChip & { name: string; color: string | null })[];
  /** The rows with a status and an event that are on. */
  rows: TimelineRow[];
  groups: TimelineGroup[];
}

/**
 * The timeline of the rows with a status that is on, with only the groups of the events that are
 * on. The status chips count the rows, with `archived` for the archived ones, whose rows are there
 * only when their chip is on. The event chips count the rows with a status that is on. Each
 * group keeps the colour that it has with all the rows.
 */
export const filterTimeline = (
  rows: TimelineRow[],
  filter: TimelineFilter,
  today: Date,
  archived = 0,
): TimelineView => {
  const countOf = (key: string): number =>
    key === "archived" ? archived : rows.filter(({ status }) => status === key).length;
  const statuses = ALL_STATUSES.map((key) => ({
    key,
    count: countOf(key),
    selected: filter.statuses.includes(key),
  })).filter(({ count }) => count);
  const unarchived = statuses.filter(({ key }) => key !== "archived");
  const total = rows.filter(({ status }) => status !== "archived").length;
  const own = rows.filter((row) => filter.statuses.includes(row.status));

  const colors = new Map(buildTimeline(rows, today).map(({ key, color }) => [key, color]));
  const all = buildTimeline(own, today).map((group) => ({
    ...group,
    color: colors.get(group.key) ?? group.color,
  }));
  const on = (key: string): boolean => !filter.hiddenEvents.includes(key);
  const groups = all.filter(({ key }) => on(key));

  return {
    statuses: [
      {
        key: "all",
        count: total,
        selected: unarchived.length > 0 && unarchived.every(({ selected }) => selected),
      },
      ...statuses,
    ],
    events: [
      {
        key: "all",
        name: "All events",
        color: null,
        count: own.length,
        selected: all.every(({ key }) => on(key)),
      },
      ...all.map(({ key, name, color, count }) => ({ key, name, color, count, selected: on(key) })),
    ],
    rows: groups.flatMap(({ stops }) => stops.flatMap((stop) => stop.rows)),
    groups,
  };
};
